import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";

import {
  deleteSelectedTasks,
  hideShareMultipleColumnsModal,
  hideStopSharingWithClientModal,
  openGroupShareMultipleColumnsModal,
  openStopSharingGroupWithClientModal,
  setSelectedTasks,
} from "src/redux";
import { SelectedTasksBar } from "src/app/components/SelectedTasksBar/SelectedTasksBar";
import { saveToLocalStorage } from "src/utils/localStorage";
import AddCreatorsCDTWorkspaceProjectModal, {
  SelectedCreator,
} from "src/app/modals/AddCreatorsCDTWorkspaceProjectModal/AddCreatorsCDTWorkspaceProjectModal";
import { generateRankString } from "src/utils/rankStrings";
import { ifRowMatchesFilters } from "src/app/project/SingleProject/TasksTable/tasksTableSorting/tableSortingFunctions/filterTableData";
import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ReactComponent as ShowcaseIcon } from "src/images/showcase.svg";
import { ReactComponent as RubberIcon } from "src/images/rubber.svg";
import { ReactComponent as DeleteIcon } from "src/images/trash-can.svg";
import { ReactComponent as ClientOrangeIcon } from "src/images/client-orange.svg";
import { ReactComponent as ClientStopShareIcon } from "src/images/client-light-grey.svg";
import { ReactComponent as DownloadIcon } from "src/images/download-rounded.svg";
import { TaskType } from "src/types";
import Loader from "src/app/components/Loader/Loader";
import { taskAvatarShape } from "src/app/components/Table/Table";
import StopSharingGroupWithClientModal from "src/app/modals/StopSharingGroupWithClientModal/StopSharingGroupWithClientModal";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import { AddToDropdownSelectedTasksBar } from "src/app/components/SelectedTasksBar/components/AddToDropdownSelectedTasksBar";
import { navigateToCreateNewProject } from "src/app/SettingsModal/methods";
import { setCreateNewProjectData } from "src/redux/main/mainActions";
import { ShareMultipleColumnsModal } from "src/app/modals/ShareMultipleColumnsModal/ShareMultipleColumnsModal";
import { RootState } from "src/redux/reducers";
import {
  CreatorShowcaseContext,
  openCreatorShowcase,
  MAX_CREATORS_FOR_GENERATE_SHOWCASE,
} from "src/app/CreatorShowcase/utils";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import {
  ProjectPermissionsEnum,
  WorkspacePermissionsEnum,
} from "src/utils/PermissionsEnums";
import { PublicationsService } from "src/services";
import ContentProposalDownloadModal from "src/app/modals/ContentDownloadModal/ContentProposalDownloadModal";
import { handleError } from "src/utils/error";
import useCheckWorkspacePermission from "src/hooks/useCheckWorkspacePermission";
import ContentProposalElementsSummaryDownloadDropdown from "../../../../dropdowns/ContentProposalElementsSummaryDownloadDropdown/ContentProposalElementsSummaryDownloadDropdown";
import AskAIButton from "./AskAIButton/AskAIButton";
import MoreSettingsDropdown from "./MoreSettingsDropdown";
import generateUuid from "src/app/libs/generateUuid";

import "./SingleProjectSelectedTasksBar.scss";

const ASK_AI_TOOLTIP_KEY = "askAITooltipHiddenSingleProject" as const;

const isTooltipOpenInitialState = (hasAccessToAskAI: boolean) => {
  const askAITooltipHidden =
    window.localStorage.getItem(ASK_AI_TOOLTIP_KEY) === "true";

  if (askAITooltipHidden || !hasAccessToAskAI) {
    const selectedTasksBarTooltipHidden = window.localStorage.getItem(
      "selectedTasksBarTooltipHidden",
    );
    if (selectedTasksBarTooltipHidden === null) {
      return true;
    }
    return !JSON.parse(selectedTasksBarTooltipHidden);
  }
  return false;
};

export const selectedCreatorsPayload = (
  selectedCreators: SelectedCreator[],
) => {
  let previousRank = "a";

  return selectedCreators.map((creator: SelectedCreator) => {
    const newRank = generateRankString(previousRank, "");
    previousRank = newRank;

    return {
      uuid: generateUuid(),
      name: creator.name,
      socialProfiles: creator.socialProfiles,
      rank: newRank,
    };
  });
};

export function SingleProjectSelectedTasksBar() {
  const {
    taskReducer: {
      tasksLoading,
      selectedTasks,
      showShareGroupMultipleColumnsModal,
      showStopSharingGroupWithClientModal,
    },
    mainReducer: { activeWorkspaceUuid, identity },
    projectReducer: { taskType, projectBasicData },
    taskFiltersReducer: { selectedFilters },
  } = useSelector((state: RootState) => state);

  const hasAccessToStartConversationWithAi =
    useCheckWorkspacePermission("AI_META_VALUE");

  const [isTooltipOpen, setIsTooltipOpen] = useState(() =>
    isTooltipOpenInitialState(hasAccessToStartConversationWithAi),
  );
  const [
    showModalAddCreatorsToWorkspaceProject,
    setShowModalAddCreatorsToWorkspaceProject,
  ] = useState(false);
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
  const [selectedCreators, setSelectedCreators] = useState<SelectedCreator[]>(
    [],
  );
  const [showContentDownloadModal, setShowContentDownloadModal] =
    useState(false);
  const [isDownloadingAllContent, setIsDownloadingAllContent] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const clearSelected = () => {
    dispatch(setSelectedTasks([]));
  };

  const deleteSelected = async () => {
    if (!projectBasicData) return;

    dispatch(deleteSelectedTasks(selectedTasks));
  };

  const handleDownloadContent = async () => {
    setIsDownloadingAllContent(true);
    try {
      await PublicationsService.downloadContent(
        selectedTasks.map((item) => item.taskId),
      );
      setShowContentDownloadModal(true);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDownloadingAllContent(false);
    }
  };

  const openShowcase = () => {
    if (taskType !== TaskType.Creator) {
      return;
    }

    const ids = [...selectedTasks].map((task) => task.taskId)?.reverse();
    openCreatorShowcase({
      history,
      context: CreatorShowcaseContext.CreatorList,
      creatorId: ids[0],
      ids,
      workspaceId: activeWorkspaceUuid,
    });
  };

  const countTaskByShareValue = (shareValue: boolean) => {
    let counter = 0;

    selectedTasks?.forEach((task) => {
      if (task?.shared === shareValue) {
        counter += 1;
      }
    });

    return counter;
  };

  const hideTooltip = () => {
    saveToLocalStorage("selectedTasksBarTooltipHidden", true);
    setIsTooltipOpen(false);
  };

  const dispatchDataAndNavigateToCreateNewProject = () => {
    dispatch(
      setCreateNewProjectData({
        selectedCreatorsPayload: selectedCreatorsPayload(selectedCreators),
      }),
    );
    navigateToCreateNewProject(history, activeWorkspaceUuid);
  };

  const renderSelectedTasksBarContent = () => {
    return (
      <>
        {hasAccessToStartConversationWithAi && (
          <AskAIButton
            selectedTasks={selectedTasks}
            tooltipLocalStorageKey={ASK_AI_TOOLTIP_KEY}
            wsWorkspaceUuid={activeWorkspaceUuid}
            wsProjectUuid={projectBasicData?.projectId ?? ""}
            onClearSelected={clearSelected}
          />
        )}
        {/* Share Project Button */}
        {projectBasicData?.permissions?.project.includes(
          ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
        ) &&
          projectBasicData?.clientMode &&
          countTaskByShareValue(false) > 0 && (
            <Button
              size="large"
              variant="white"
              onClick={() =>
                dispatch(openGroupShareMultipleColumnsModal(selectedTasks))
              }
              data-action="share"
            >
              <ClientOrangeIcon className="selected-tasks-bar__shared-icon" />
              <IDHFormattedMessage id="ws_share" defaultMessage="Share" />
            </Button>
          )}
        {/* Stop Share Project Button */}
        {projectBasicData?.permissions?.project.includes(
          ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
        ) &&
          projectBasicData?.clientMode &&
          countTaskByShareValue(true) > 0 && (
            <Button
              size="large"
              variant="white"
              onClick={() =>
                dispatch(openStopSharingGroupWithClientModal(selectedTasks))
              }
              data-action="stopShare"
            >
              <ClientStopShareIcon />
              <IDHFormattedMessage
                id="ws_stop_share"
                defaultMessage="Stop share"
              />
            </Button>
          )}
        {/* Generate Showcase Button */}
        {projectBasicData?.permissions?.project.includes(
          ProjectPermissionsEnum.DOWNLOAD_REPORT,
        ) &&
          taskType === TaskType.Creator && (
            <TooltipPortal
              content={
                <IDHFormattedMessage
                  id="ws_cannot_generate_showcase"
                  defaultMessage="Cannot generate showcase for more than 100 creators"
                />
              }
              contentHidden={
                selectedTasks.length <= MAX_CREATORS_FOR_GENERATE_SHOWCASE
              }
              up
            >
              <Button
                size="large"
                variant="white"
                onClick={openShowcase}
                data-action="generateShowcase"
                disabled={
                  selectedTasks.length > MAX_CREATORS_FOR_GENERATE_SHOWCASE
                }
              >
                <ShowcaseIcon />
                <IDHFormattedMessage
                  id="ws_generate_showcase"
                  defaultMessage="Generate showcase"
                />
              </Button>
            </TooltipPortal>
          )}

        {/* Add to Dropdown Component */}
        {taskType === TaskType.Creator &&
          projectBasicData?.permissions?.project?.includes(
            ProjectPermissionsEnum.TASK_MANAGEMENT ||
              identity?.permissions?.workspace?.includes(
                WorkspacePermissionsEnum.CREATOR_DATABASE,
              ),
          ) && (
            <AddToDropdownSelectedTasksBar
              setSelectedCreators={setSelectedCreators}
              setShowModalAddCreatorsToWorkspaceProject={
                setShowModalAddCreatorsToWorkspaceProject
              }
            />
          )}
        {taskType === TaskType.Content && (
          <ContentProposalElementsSummaryDownloadDropdown
            setShowContentDownloadModal={setShowContentDownloadModal}
            wsTaskUuids={selectedTasks?.map(
              (selectedTask) => selectedTask.taskId,
            )}
            wsProjectUuid={projectBasicData?.projectId ?? null}
          >
            <Button variant="white" size="small" textWithIcon>
              <DownloadIcon />
              <span>
                <IDHFormattedMessage
                  id="ws_download"
                  defaultMessage="Download"
                />
              </span>
            </Button>
          </ContentProposalElementsSummaryDownloadDropdown>
        )}

        {taskType === TaskType.Publication && (
          <Button
            variant="white"
            size="small"
            textWithIcon
            onClick={handleDownloadContent}
            disabled={isDownloadingAllContent}
          >
            <DownloadIcon />
            <IDHFormattedMessage
              id="ws_download_published_content"
              defaultMessage="Download published content"
            />
          </Button>
        )}

        {/* Clear Selected Button */}
        <Button
          size="large"
          variant="white"
          onClick={clearSelected}
          disabled={!selectedTasks.length}
          data-action="clearSelected"
        >
          <RubberIcon />
          <IDHFormattedMessage id="ws_clear_selected" defaultMessage="Clear" />
        </Button>

        {/* Delete Selected Button */}
        {tasksLoading ? (
          <Loader />
        ) : (
          projectBasicData?.permissions?.project.includes(
            ProjectPermissionsEnum.TASK_MANAGEMENT,
          ) && (
            <Button
              size="large"
              variant="red-text"
              onClick={() => setShowDeleteTaskModal(true)}
              disabled={!selectedTasks.length}
              data-action="deleteSelected"
            >
              <DeleteIcon />
              <IDHFormattedMessage id="ws_remove" defaultMessage="Remove" />
            </Button>
          )
        )}

        <MoreSettingsDropdown
          taskType={taskType}
          selectedTasks={selectedTasks}
          onClearSelected={clearSelected}
          tasksLoading={tasksLoading}
        />

        {/* Confirmation and Sharing Modals */}
        {showDeleteTaskModal && (
          <RemoveModal
            onClose={() => setShowDeleteTaskModal(false)}
            objectNames={selectedTasks?.map((task) => task?.title) ?? []}
            removeFunction={() => {
              deleteSelected();
              setShowDeleteTaskModal(false);
            }}
          />
        )}
        {showShareGroupMultipleColumnsModal && (
          <ShareMultipleColumnsModal
            onClose={() => dispatch(hideShareMultipleColumnsModal())}
            avatarShape={taskAvatarShape.Square}
            tasks={selectedTasks}
            taskType={taskType}
            shouldSelectTasksAfterSharing
          />
        )}
        {showStopSharingGroupWithClientModal && (
          <StopSharingGroupWithClientModal
            onClose={() => dispatch(hideStopSharingWithClientModal())}
            taskAvatarShape={taskAvatarShape.Square}
            tasks={selectedTasks}
          />
        )}
        {/* Add Creators Modal */}
        {showModalAddCreatorsToWorkspaceProject && (
          <AddCreatorsCDTWorkspaceProjectModal
            onClose={() => setShowModalAddCreatorsToWorkspaceProject(false)}
            activeProjectUuid={projectBasicData?.projectId}
            selectedCreators={selectedCreators}
            openCreateNewProjectModal={
              dispatchDataAndNavigateToCreateNewProject
            }
          />
        )}
        {showContentDownloadModal && (
          <ContentProposalDownloadModal
            onClose={() => {
              setShowContentDownloadModal(false);
            }}
          />
        )}
      </>
    );
  };

  useEffect(() => {
    if (Object.keys(selectedFilters).length === 0) return;

    const filteredTasks = selectedTasks.filter((task) =>
      ifRowMatchesFilters(task, selectedFilters, identity.id),
    );

    // As we are only filtering out elements, comparing length is enough
    const hasChanged = filteredTasks.length !== selectedTasks.length;

    if (hasChanged) {
      dispatch(setSelectedTasks(filteredTasks));
    }
  }, [dispatch, identity.id, selectedFilters, selectedTasks]);

  return (
    <SelectedTasksBar
      selectedTasksLength={selectedTasks.length}
      isTooltipOpen={isTooltipOpen}
      hideTooltip={hideTooltip}
    >
      {renderSelectedTasksBarContent()}
    </SelectedTasksBar>
  );
}
